import { AxiosError, AxiosInstance } from "axios";

// The guard answers 422 rather than 401 so the JWT interceptor does not mistake a
// lapsed step-up pass for a dead session and sign the partner out. `reauth_required`
// is the machine-readable marker that distinguishes it from any other 422.
const REAUTH_REQUIRED_CODE = "reauth_required";

// True when a request was refused because this session needs a fresh OTP. Local to
// this module: the interceptor below is the only thing that has to recognise it.
const isReAuthRequiredError = (error: unknown): boolean =>
  (
    error as {
      response?: { data?: { error?: { code?: string } } };
    }
  )?.response?.data?.error?.code === REAUTH_REQUIRED_CODE;

/**
 * Rejection raised when the partner dismisses the OTP prompt instead of completing it.
 *
 * A cancel is not a failure: it travels the same path as a real error (out of the
 * interceptor, into the caller's `onError`), so handlers need a way to tell the two
 * apart and stay quiet for this one.
 */
export class StepUpDismissedError extends Error {
  constructor() {
    super("Re-authentication was dismissed");
    this.name = "StepUpDismissedError";
  }
}

/** True when the partner dismissed the OTP prompt rather than completing it. */
export const isStepUpDismissedError = (error: unknown): boolean =>
  error instanceof StepUpDismissedError;

/**
 * Collects an OTP from the partner. Registered by StepUpAuthProvider, which owns the
 * only ReAuthModal in the app; the interceptor cannot render one itself.
 *
 * Resolves once a code has been accepted (the server has stamped the session), and
 * rejects if the partner dismisses the prompt.
 */
type StepUpPrompt = () => Promise<void>;

let promptForStepUp: StepUpPrompt | null = null;

export const setStepUpPrompt = (prompt: StepUpPrompt | null) => {
  promptForStepUp = prompt;
};

/**
 * Turns a `reauth_required` refusal into a prompt and a replay, so no call site has to
 * know that step-up auth exists. Mirrors the 401-refresh-retry interceptor in
 * `api/jwt.ts`: satisfy the requirement, then re-issue the original request untouched.
 *
 * The replay carries no token. Verifying the OTP opens the pass on the login session
 * itself (BF-409), so the same request simply succeeds the second time. That does mean
 * the API must already serve the session-backed pass — it is the only thing that emits
 * `reauth_required`, so against an older API this interceptor never fires.
 */
export const attachStepUpInterceptor = (client: AxiosInstance) => {
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const cfg = error.config;

      if (!cfg || !isReAuthRequiredError(error)) throw error;
      // One prompt per request: a second refusal is a real failure, not a stale pass.
      if (cfg.__reauthRetried || cfg.skipStepUpAuth) throw error;
      // No prompt mounted (an unauthenticated screen, or a bare API client) — surface
      // the refusal rather than hanging on a promise nothing will settle.
      if (!promptForStepUp) throw error;

      await promptForStepUp();

      cfg.__reauthRetried = true;
      return client(cfg);
    }
  );
};
